import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Box, Button, Collapse, Group, ScrollArea, Stack, Text } from "@mantine/core"
import { useEffect, useRef } from "react"
import type { IStreamingSegment } from "../../../hooks/useStreamingTranscript"
import type { ISpeakerVM } from "../../../lib/speaker-vm"
import { suggestedSpeakerVM } from "../../RecordingSurface/SpeakerHypothesisStrip"
import { SpeakerChip } from "../../SpeakerChip"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * Collapsible live transcript panel with auto-scroll + jump-to-latest. Owns
 * its own scroll-area ref and the "user scrolled up" detection so the smart
 * wrapper doesn't have to thread DOM refs back through props. Auto-scroll
 * triggers on segment count + interim text changes.
 */
export const LiveTranscriptPanel = ({
  segments,
  interimText,
  interimSpeaker,
  hypotheses,
  pending,
  onAction,
  showTranscript,
  onToggleVisible,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const scrollAreaRef = useRef<HTMLDivElement | null>(null)
  const userScrolledUpRef = useRef(false)

  const suggestedRows = hypotheses
    .filter((h) => h.status === "tentative")
    .map(suggestedSpeakerVM)
    .filter((vm): vm is ISpeakerVM => vm !== null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — scroll triggers on new content
  useEffect(() => {
    const viewport = scrollAreaRef.current
    if (!viewport || userScrolledUpRef.current) return
    viewport.scrollTop = viewport.scrollHeight
  }, [segments.length, interimText])

  return (
    <Stack gap="xs" mt="sm">
      <Group justify="space-between">
        <Text size="xs" fw={600} c="dimmed">
          <Trans>Live transcript</Trans>
        </Text>
        <Button variant="subtle" size="compact-xs" onClick={onToggleVisible}>
          {showTranscript ? i18n._(msg`Hide`) : i18n._(msg`Show`)}
        </Button>
      </Group>
      <Collapse in={showTranscript}>
        <Box pos="relative">
          <ScrollArea
            h={200}
            type="auto"
            viewportRef={scrollAreaRef}
            onScrollPositionChange={() => {
              const viewport = scrollAreaRef.current
              if (!viewport) return
              const atBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 20
              userScrolledUpRef.current = !atBottom
            }}
          >
            <Stack gap={2}>
              {suggestedRows.length > 0 && (
                <Group gap="xs" wrap="wrap" mb="xs">
                  {suggestedRows.map((vm) => (
                    <SpeakerChip
                      key={vm.label ?? vm.displayName}
                      speaker={vm}
                      variant="inline"
                      suggestionPending={pending}
                      onConfirmSuggestion={(label, name) => onAction(label, name, "confirm")}
                      onRejectSuggestion={(label, name) => onAction(label, name, "reject")}
                    />
                  ))}
                </Group>
              )}
              {segments.map((seg: IStreamingSegment) => (
                <Text key={`${seg.speaker}-${seg.start}-${seg.end}`} size="xs" lh={1.4}>
                  <Text span fw={600} c="info">
                    {seg.speaker}:
                  </Text>{" "}
                  {seg.words
                    ? seg.words.map((w, wIdx) => (
                        <Text
                          key={`${seg.start}-${w.start}`}
                          span
                          c={w.confidence >= 0.9 ? undefined : w.confidence >= 0.7 ? "warning.7" : "warning.6"}
                          title={`confidence: ${(w.confidence * 100).toFixed(0)}%`}
                        >
                          {wIdx > 0 ? " " : ""}
                          {w.word}
                        </Text>
                      ))
                    : seg.text}
                </Text>
              ))}
              {interimText && (
                <Text size="xs" lh={1.4} c="dimmed" fs="italic">
                  <Text span fw={600}>
                    {interimSpeaker}:
                  </Text>{" "}
                  {interimText}
                </Text>
              )}
            </Stack>
          </ScrollArea>
          {userScrolledUpRef.current && (
            <Button
              size="compact-xs"
              variant="light"
              fullWidth
              pos="absolute"
              bottom={4}
              left={0}
              right={0}
              className={classes.jumpToLatestBtn}
              onClick={() => {
                userScrolledUpRef.current = false
                const viewport = scrollAreaRef.current
                if (viewport) viewport.scrollTop = viewport.scrollHeight
              }}
            >
              Jump to latest
            </Button>
          )}
        </Box>
      </Collapse>
    </Stack>
  )
}
